import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import FoodForm from "../modal/form";
import { useGetFoodsQuery } from "@/services/foodsApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import { categories } from "@/lib/constants/food";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { CreateFoodTypes } from "@/types/foods";
import { displayValue } from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { useGetFoodCategoriesQuery } from "@/services/commonApi";
const { VITE_VIEW_S3_URL } = import.meta.env;
interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  total_nutrition?: number;
  fat?: number;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "foods",
};

export default function FoodsTableView() {
  const food = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).food ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<CreateFoodTypes | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: foodData,
    isLoading,
    refetch,
  } = useGetFoodsQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const { data: foodCategoriesOptions } = useGetFoodCategoriesQuery()

  const foodstableData = React.useMemo(() => {
    return Array.isArray(foodData?.data) ? foodData?.data : [];
  }, [foodData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const actionsColumn: ColumnDef<CreateFoodTypes> = {
    accessorKey: "action",
    header: ({ table }) => <span>Action</span>,
    cell: ({ row }) => (
      <DataTableRowActions
        access={food}
        handleEdit={handleEdit}
        data={row.original}
        refetch={refetch}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };

  const columns: ColumnDef<CreateFoodTypes>[] = [
    {
      accessorKey: "name",
      meta: "Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            {row.original.img_url ? (
              <img
                src={
                  row.original.img_url.includes('https://')
                    ? row.original.img_url
                    : `${VITE_VIEW_S3_URL}/${row.original.img_url}`
                }
                alt={row.original.name}
                loading="lazy"
                className="size-8 bg-gray-100 object-cover rounded-sm "
              />
            ) : (
              <div className="size-8 bg-gray-100 rounded-sm"></div>
            )}
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      <span>
                        {displayValue(
                          `${row.original.name}`.length > 15
                            ? `${row.original.name}`.substring(0, 15) + "..."
                            : `${row.original.name}`
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(`${row?.original?.name}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "brand",
      meta: "Brand",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Brand"
          sortKey="brand"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.brand}`.length > 8
                          ? `${row.original.brand}`.substring(0, 8) + "..."
                          : `${row.original.brand}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="capitalize text-sm">
                    {displayValue(`${row?.original?.brand}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "category",
      meta: "Category",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Category"
          sortKey="category"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.category}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "total_nutrition",
      meta: "Total Nutrition (g)",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Nutrition (g)"
          sortKey="total_nutrition"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.total_nutrition}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "fat",
      meta: "Total Fat",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Fat"
          sortKey="fat"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.fat}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(food !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: foodstableData as CreateFoodTypes[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleEdit = (data: CreateFoodTypes) => {
    setAction("edit");
    setData(data);
    setIsDialogOpen(true);
  };

  const totalRecords = foodData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (
    field: string,
    value: string|number | React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = typeof value === "string" ? value : typeof value === "number" ? value : value.target.value;

    setFilterData((prev) => {
      const newFilter = { ...prev };

      if (typeof newValue === "string" && newValue.trim() === "") {
        delete newFilter[field];
      } else {
        newFilter[field] = newValue;
      }

      return newFilter;
    });
  };

  const filterDisplay = [
    {
      type: "combobox",
      name: "category_id",
      label: "Food Category",
      options: foodCategoriesOptions,
      function: (value: string) => handleFilterChange("category_id", value),
    },
    {
      type: "number",
      name: "total_nutrition",
      label: "Total Nutrition",
      function: (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFilterChange("total_nutrition", e),
    },
    {
      type: "number",
      name: "total_fat",
      label: "Total Fat",
      function: (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFilterChange("total_fat", e),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-initial sm:flex-1 min-w-0">
          <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1 sm:gap-2 shrink-0">
          {food !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center justify-center h-8 px-2 min-w-[32px]"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline ml-1">Create New</span>
            </Button>
          )}
          <button
            className={`border rounded-full size-8 text-gray-400 flex items-center justify-center shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : foodstableData.length > 0 ? (
          foodstableData.map((row: CreateFoodTypes) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm relative space-y-2"
              onClick={() => food !== "read" && handleEdit(row)}
            >
              <div className="flex justify-between items-start">
                <div className="flex gap-3 items-center">
                  {row.img_url ? (
                    <img
                      src={
                        row.img_url.includes('https://')
                          ? row.img_url
                          : `${VITE_VIEW_S3_URL}/${row.img_url}`
                      }
                      alt={row.name}
                      loading="lazy"
                      className="size-12 bg-gray-100 object-cover rounded-md"
                    />
                  ) : (
                    <div className="size-12 bg-gray-100 rounded-md"></div>
                  )}
                  <div>
                    <h3 className="font-bold text-base capitalize">{row.name}</h3>
                    <p className="text-xs text-gray-400 capitalize">{row.brand || "No Brand"}</p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DataTableRowActions
                    access={food}
                    handleEdit={handleEdit}
                    data={row}
                    refetch={refetch}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1 pt-2 border-t text-sm text-gray-500">
                <div>
                  <strong>Category:</strong> {row.category}
                </div>
                <div>
                  <strong>Total Fat:</strong> {row.fat}
                </div>
                <div>
                  <strong>Total Nutrition (g):</strong> {row.total_nutrition}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No records found.
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (food !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEdit(row.original)
                      }
                    }}
                    className={food !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No food found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {foodstableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <FoodForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </div>
  );
}
